import Image from 'next/image';
import { snakeCase } from 'lodash-es';

import { Box, Heading, Text } from '@chakra-ui/react';
import { ArrowRightIcon } from '@udacity/chakra-uds-icons';

import { ButtonLink } from '~/components/button-link';
import { ContentContainer } from '~/components/container';
import { Markdown } from '~/components/markdown';
import { SanityImage } from '~/components/sanity-image';
import { trackEvent } from '~/features/analytics/track-event';
import { SanityImageType } from '~/features/page-template/models/image';

type HeroSectionProps = {
  title: string;
  browText: string | null;
  description: string;
  slug: string;
  image: SanityImageType | null;
  catalogUrl: string;
};

export function HeroSection({ browText, title, description, image, catalogUrl }: HeroSectionProps) {
  return (
    <Box as='section' overflow='hidden' position='relative'>
      <Box
        height='100%'
        left='50%'
        position='absolute'
        width='2000px'
        sx={{
          '> img': {
            objectPosition: {
              base: 'bottom',
              xl: 'top',
            },
          },
        }}
        top={{
          base: '50%',
          xl: '0px',
        }}
        transform={{
          base: 'translate(-50%, -50%)',
          xl: 'translateX(-50%)',
        }}
      >
        <Box
          bottom={0}
          height='100%'
          left={0}
          opacity={0.4}
          pointerEvents='none'
          position='absolute'
          right={0}
          top={{ base: 230, md: 0 }}
          width='100%'
        >
          <Image
            alt=''
            sizes='2000px'
            src='/images/backgrounds/lines-2.png'
            style={{
              objectFit: 'contain',
            }}
            fill
            priority
          />
        </Box>
      </Box>
      <ContentContainer
        alignItems='center'
        display='flex'
        position='relative'
        flexDir={{
          base: 'column',
          md: 'row',
        }}
      >
        <Box
          flex={{
            md: '0 1 448px',
          }}
          padding={{
            base: '24px 0px',
            md: '95px 0px',
          }}
        >
          {Boolean(browText) && <Text size='caption'>{browText}</Text>}
          <Heading as='h1' size='h1'>
            {title}
          </Heading>
          <Box h='24px' />
          <Markdown markdownText={description} />
          <Box h='12px' />
          <ButtonLink
            buttonProps={{
              'aria-label': `View Programs`,
              variant: 'link',
              display: 'inline-flex',
              alignItems: 'center',
              onClick: () =>
                trackEvent({
                  type: 'click',
                  name: 'Schools Page CTA Clicked',
                  label: `view_all_${snakeCase(title)}_programs_clicked`,
                }),
            }}
            linkProps={{
              href: catalogUrl,
            }}
          >
            View Programs <ArrowRightIcon height={6} width={6} />
          </ButtonLink>
        </Box>
        {image && (
          <Box
            alignSelf='flex-end'
            flex={{
              md: '1 0 560px',
            }}
          >
            <SanityImage height={444} image={image} quality={95} width={640} priority />
          </Box>
        )}
      </ContentContainer>
    </Box>
  );
}
